import React, { useEffect, useState } from 'react';
import SurveyContainer, {
  NavButtonProps,
} from '../../../components/Survey/Container';
import Title from '../../../components/Survey/Title';
import { useTranslation } from 'react-i18next';
import { Logo } from '../../../components/Survey/icons';
import ImageHeader from '../../../components/Survey/ImageHeader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSupplierSurvey } from '../hooks';
import NotFound from '../../../views/components/NotFound';
import PageLoading from '../../../components/PageLoading';
import {
  ErrorBuilder,
  SchemaErrors,
  extractSchemaErrors,
  mapError,
} from '../../../utils/validation';
import { Box } from '@mui/material';
import TextField, { TextFieldProps } from 'src/components/TextField';
import {
  StyledContainer,
  StyledQuestionTitle,
} from '../../../views/components/QuestionnaireV3/Question/index.styles';
import assets from '../assets';
import { GhgSupplierSurveyAnswerInSchema } from '../../interfaces';
import { toast } from 'react-toastify';
import { registrationTokenParam } from '../utils';

type FormState = GhgSupplierSurveyAnswerInSchema;
type FormErrors = SchemaErrors<FormState>;

export const validateForm = (data: Partial<FormState>): FormErrors => {
  const builder = new ErrorBuilder(data).checkTruthy(
    ['company_name', 'vat_identifier', 'contact_email'],
    'validation.required'
  );

  return builder.build();
};

export default function SupplierSurveyInitialQuestions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const survey = useSupplierSurvey();

  const [searchParams, _] = useSearchParams();
  const vatIdentifier = searchParams.get('vat_identifier');
  const registrationToken = searchParams.get('registration_token');

  const [form, setForm] = useState<Partial<FormState>>({
    company_name: '',
    vat_identifier: vatIdentifier ?? '',
    contact_email: '',
    has_sedex_questionnaire: null,
    wants_to_use_platform: null,
    zc_code: null,
    saq_file: null,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    const answer = survey.data?.answer;
    if (!!answer) {
      setForm(answer);
      return;
    }

    const prefillCompanyName = survey.data?.prefill_company_name;
    const prefillContactEmail = survey.data?.prefill_contact_email;
    if (!prefillCompanyName && !prefillContactEmail) return;

    setForm((prev) => ({
      ...prev,
      company_name: prev.company_name || prefillCompanyName || '',
      contact_email: prev.contact_email || prefillContactEmail || '',
    }));
  }, [survey.data]);

  if (survey.isError) return <NotFound />;
  if (!survey.data) return <PageLoading />;

  const prevButton: NavButtonProps = {
    label: 'ghg.supplierSurvey.back',
    onClick: () => navigate(`/suppliers/${registrationToken}`),
  };

  const nextButton: NavButtonProps = {
    label: 'ghg.supplierSurvey.next',
    onClick: () => {
      const errors = validateForm(form);
      setErrors(errors);
      if (Object.keys(errors).length > 0) return;
      const validForm = form as FormState;
      survey.update
        .mutateAsync(validForm)
        .then(() =>
          navigate(`../main-page${registrationTokenParam(registrationToken)}`)
        )
        .catch((err) => {
          setErrors(extractSchemaErrors(err, validForm));
          toast.error(String(t('ghg.supplierSurvey.submitError')));
        });
    },
  };

  const textFieldProps = (
    field: keyof FormState
  ): Partial<TextFieldProps<undefined>> => {
    return {
      value: form[field] || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm({ ...form, [field]: e.target.value }),
      errorText: mapError(errors, field, form[field]),
    };
  };

  return (
    <SurveyContainer
      logo={<Logo />}
      header={
        <ImageHeader
          imageSrc={assets.initialQuestions.image}
          text={t('ghg.supplierSurvey.mainHeader')}
          small
        />
      }
      prevLink={registrationToken !== null ? prevButton : undefined}
      nextLink={nextButton}
    >
      <Box>
        <Title title={t('ghg.supplierSurvey.initialQuestions.title')} />
        <Box display="flex" flexDirection="column">
          <StyledContainer>
            <StyledQuestionTitle>
              {t('ghg.supplierSurvey.initialQuestions.q01.title')}
            </StyledQuestionTitle>
            <TextField
              fullWidth
              label={t('ghg.supplierSurvey.initialQuestions.q01.label')}
              placeholder={t(
                'ghg.supplierSurvey.initialQuestions.q01.placeholder'
              )}
              {...textFieldProps('company_name')}
            />
          </StyledContainer>
          <StyledContainer>
            <StyledQuestionTitle>
              {t('ghg.supplierSurvey.initialQuestions.q02.title')}
            </StyledQuestionTitle>
            <TextField
              fullWidth
              label={t('ghg.supplierSurvey.initialQuestions.q02.label')}
              placeholder={t(
                'ghg.supplierSurvey.initialQuestions.q02.placeholder'
              )}
              {...textFieldProps('contact_email')}
              errorText={
                mapError(errors, 'contact_email', form.contact_email) &&
                'validation.invalidEmail'
              }
            />
          </StyledContainer>
        </Box>
      </Box>
    </SurveyContainer>
  );
}
